import {} from '../types.js';
import { add, del, set } from './map-immutable-fns.js';
/**
 * Returns an {@link IMapImmutable}.
 * Use {@link Maps.mutable} as a mutable alternatve.
 *
 * @example Basic usage
 * ```js
 * // Creating
 * let m = map();
 * // Add
 * m = m.set("name", "sally");
 * // Recall
 * m.get("name");
 * ```
 *
 * @example Enumerating
 * ```js
 * for (const [key, value] of map.entries()) {
 *  console.log(`${key} = ${value}`);
 * }
 * ```
 *
 * @example Overview
 * ```js
 * // Create
 * let m = map();
 * // Add as array or key & value pair
 * m = m.add(["name" , "sally"]);
 * m = m.add({ key: "name", value: "sally" });
 * // Add using the more typical set
 * m = m.set("name", "sally");
 * m.get("name");   // "sally";
 * m.has("age");    // false
 * m.has("name");   // true
 * m.isEmpty;       // false
 * m = m.delete("name");
 * m.entries();     // Iterator of key value pairs
 * ```
 *
 * Since it is immutable, `add()`, `delete()` and `clear()` return a new version with change.
 *
 * @param dataOrMap Optional initial data in the form of an array of `{ key: value }` or `[ key, value ]`
 */
export const immutable = (dataOrMap) => {
    if (dataOrMap === undefined)
        return immutable([]);
    if (Array.isArray(dataOrMap))
        return immutable(add(new Map(), ...dataOrMap));
    const data = dataOrMap;
    return {
        add: (...itemsToAdd) => {
            const s = add(data, ...itemsToAdd);
            return immutable(s);
        },
        set: (key, value) => {
            const s = set(data, key, value);
            return immutable(s);
        },
        get: (key) => data.get(key),
        delete: (key) => immutable(del(data, key)),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        clear: () => immutable(),
        has: (key) => data.has(key),
        entries: () => data.entries(),
        values: () => data.values(),
        isEmpty: () => data.size === 0,
    };
};
